import { useCallback, useEffect, useRef, useState } from 'react'
import { getConsultantSubmittals } from '@/services/submittalService'

// One page size for every listing this hook serves, and the same ten the rest of
// the app's tables show. Not gated on the phase-2 flag: how many rows a page holds
// is not a property of a data element. The CSV preview paginates its own rows.
export const PAGE_SIZE = 10

/**
 * The single description of a request. What gets sent and what decides a new
 * request are the same thing, so a parameter added here cannot be missed by a
 * separate dependency list.
 *
 * `extraParams` carries filter parameters declared by a data element's config,
 * which cannot be allowlisted here because they are not known at build time.
 * Merged last.
 *
 * `payloadFields` names the payload groups to extract. A configured listing
 * derives it from its column keys, since the API omits payload it is not asked
 * for. Precast configures six root columns, so it sends none.
 */
const buildParams = ({
  page,
  pageSize,
  search,
  sort,
  order,
  dataElementId,
  extraParams,
  payloadFields,
}) => {
  const params = {
    page: page || 1,
    page_size: pageSize || PAGE_SIZE,
  }
  if (search) params.search = search
  if (sort) params.sort = sort
  if (order) params.order = order
  if (dataElementId) params.data_element_id = [dataElementId]
  if (payloadFields?.length) params.payload_fields_to_extract = payloadFields
  return Object.assign(params, extraParams || {})
}

/**
 * Rows for a configured listing, plus its pagination.
 *
 * Ported from pitstop-ui's useSubmittals. `loading` starts true when the hook is
 * enabled: no rows and not loading is what the table renders as "no submissions
 * yet", and the effect that starts the request only runs after the first paint —
 * so the honest initial state is "loading", not "empty".
 */
const useSubmittals = (isEnabled, options = {}) => {
  const [submittals, setSubmittals] = useState([])
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 })
  const [loading, setLoading] = useState(isEnabled)
  const [error, setError] = useState(null)
  const requestIdRef = useRef(0)

  // Returned below as well as sent: the pager labels itself with the page size
  // that was actually requested, so the two cannot disagree.
  const pageSize = PAGE_SIZE

  // Content, not identity: callers build `extraParams` inline, so a new object
  // every render would loop. Serialised once and used both as the request and as
  // its own dependency.
  const paramsKey = JSON.stringify(buildParams({ ...options, pageSize }))

  const fetchSubmittals = useCallback(() => {
    if (!isEnabled) return
    setLoading(true)
    setError(null)

    const params = JSON.parse(paramsKey)
    // A slower earlier request must not overwrite a later one's rows — a fast
    // typist outruns the debounce often enough for this to matter.
    const currentRequestId = ++requestIdRef.current

    getConsultantSubmittals(params)
      .then(response => {
        if (currentRequestId !== requestIdRef.current) return
        setSubmittals(response?.data || [])
        setMeta({
          total: response?.pagination?.totalItems || 0,
          totalPages: response?.pagination?.totalPages || 1,
        })
        setLoading(false)
      })
      .catch(() => {
        if (currentRequestId !== requestIdRef.current) return
        setError('Failed to load submittals.')
        setLoading(false)
      })
  }, [isEnabled, paramsKey])

  useEffect(() => {
    fetchSubmittals()
  }, [fetchSubmittals])

  return { submittals, meta, loading, error, pageSize, refetch: fetchSubmittals }
}

export default useSubmittals
